import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LifeBuoy, Send, ChevronLeft, Plus, X, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { timeAgo } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export const TICKET_CATEGORIES = ["order_shipping", "payment", "report", "account", "other"];
const STATUS_COLORS = { open: "bg-emerald-100 text-emerald-700", closed: "bg-slate-200 text-slate-600" };

export default function Support() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusTicket = searchParams.get("ticket");

  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ category: "order_shipping", subject: "", message: "" });
  const endRef = useRef();

  useEffect(() => { if (user === false) navigate("/auth"); }, [user, navigate]);

  const loadTickets = useCallback(async () => {
    try { const { data } = await api.get("/support/tickets"); setTickets(data); } catch {}
  }, []);
  useEffect(() => { if (user) loadTickets(); }, [user, loadTickets]);

  const openTicket = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setActive(data); setCreating(false);
      loadTickets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [loadTickets]);

  useEffect(() => { if (focusTicket) openTicket(focusTicket); }, [focusTicket, openTicket]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.messages]);

  // poll active ticket
  useEffect(() => {
    if (!active?.id) return;
    const iv = setInterval(async () => {
      try { const { data } = await api.get(`/support/tickets/${active.id}`); setActive(data); } catch {}
    }, 6000);
    return () => clearInterval(iv);
  }, [active?.id]);

  const submitNew = async () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success(t("support.created"));
      setForm({ category: "order_shipping", subject: "", message: "" });
      setCreating(false);
      await loadTickets();
      setSearchParams({ ticket: data.id });
      openTicket(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    const body = text.trim(); setText("");
    try { await api.post(`/support/tickets/${active.id}/messages`, { text: body }); openTicket(active.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const closeTicket = async () => { await api.post(`/support/tickets/${active.id}/close`); openTicket(active.id); };
  const reopenTicket = async () => { await api.post(`/support/tickets/${active.id}/reopen`); openTicket(active.id); };

  return (
    <div className="px-0 sm:px-8 py-0 sm:py-6">
      <div className="bg-white sm:border sm:border-slate-200 sm:rounded-3xl overflow-hidden flex h-[calc(100vh-4rem)] sm:h-[calc(100vh-8rem)]">
        {/* Ticket list */}
        <div className={`w-full sm:w-80 border-r border-slate-200 flex-col ${active || creating ? "hidden sm:flex" : "flex"}`}>
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h1 className="font-display font-bold text-xl flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-[#008080]" />{t("support.title")}</h1>
            <Button data-testid="new-ticket-btn" onClick={() => { setCreating(true); setActive(null); }} size="sm" className="h-8 bg-[#008080] hover:bg-[#006666]"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {tickets.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">{t("support.noTickets")}</div>
            ) : tickets.map((tk) => (
              <button key={tk.id} data-testid={`ticket-${tk.id}`} onClick={() => { setSearchParams({ ticket: tk.id }); openTicket(tk.id); }}
                className={`w-full text-left p-3 hover:bg-slate-50 border-b border-slate-50 ${active?.id === tk.id ? "bg-teal-50/50" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-sm truncate">{tk.subject}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${STATUS_COLORS[tk.status]}`}>{t(`support.status.${tk.status}`)}</span>
                </div>
                <p className="text-xs text-slate-400 truncate mt-0.5">{t(`support.categories.${tk.category}`)} · {tk.last_message}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Right pane */}
        <div className={`flex-1 flex-col ${active || creating ? "flex" : "hidden sm:flex"}`}>
          {creating ? (
            <div className="flex-1 overflow-y-auto p-6 max-w-xl w-full mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <button className="sm:hidden" onClick={() => setCreating(false)}><ChevronLeft className="h-5 w-5" /></button>
                <h2 className="font-display font-bold text-lg">{t("support.newTicket")}</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>{t("support.category")}</Label>
                  <select data-testid="ticket-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-teal-500/40">
                    {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{t(`support.categories.${c}`)}</option>)}
                  </select>
                </div>
                <div><Label>{t("support.subject")}</Label>
                  <Input data-testid="ticket-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1" /></div>
                <div><Label>{t("support.message")}</Label>
                  <Textarea data-testid="ticket-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-1" /></div>
                <div className="flex items-start gap-2 text-xs text-teal-700 bg-teal-50 rounded-lg p-3">
                  <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" /><span>{t("support.hint")}</span>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="ticket-submit-btn" onClick={submitNew} disabled={!form.subject.trim() || !form.message.trim()} className="bg-[#008080] hover:bg-[#006666]">{t("support.send")}</Button>
                  <Button variant="outline" onClick={() => setCreating(false)}><X className="h-4 w-4 mr-1" />{t("support.cancel")}</Button>
                </div>
              </div>
            </div>
          ) : !active ? (
            <div className="flex-1 grid place-items-center text-slate-400 text-sm">{t("support.selectTicket")}</div>
          ) : (
            <>
              <div className="p-3 border-b border-slate-100 flex items-center gap-3">
                <button className="sm:hidden" onClick={() => setActive(null)}><ChevronLeft className="h-5 w-5" /></button>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{active.subject}</p>
                  <p className="text-xs text-slate-400">{t(`support.categories.${active.category}`)}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[active.status]}`}>{t(`support.status.${active.status}`)}</span>
                {active.status === "open"
                  ? <Button data-testid="ticket-close-btn" onClick={closeTicket} size="sm" variant="outline" className="h-8 text-slate-600">{t("support.close")}</Button>
                  : <Button data-testid="ticket-reopen-btn" onClick={reopenTicket} size="sm" variant="outline" className="h-8 text-[#008080] border-[#008080]">{t("support.reopen")}</Button>}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/50">
                {(active.messages || []).map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#008080] text-white" : m.sender_role === "admin" ? "bg-teal-50 border border-teal-200 text-teal-900" : "bg-white border border-slate-200"}`}>
                        {!mine && <p className="text-[10px] font-semibold mb-0.5 opacity-70">{m.sender_role === "admin" ? t("support.supportTeam") : m.sender_name}</p>}
                        <p className="whitespace-pre-line">{m.text}</p>
                        <p className={`text-[10px] mt-1 ${mine ? "text-teal-100" : "text-slate-400"}`}>{timeAgo(m.created_at, lang)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              {active.status === "open" ? (
                <div className="p-3 border-t border-slate-100 flex items-center gap-2">
                  <input data-testid="ticket-reply-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder={t("support.typeMessage")} className="flex-1 bg-slate-100 rounded-full px-4 h-11 outline-none text-sm" />
                  <Button data-testid="ticket-send-btn" onClick={send} className="h-11 w-11 p-0 rounded-full bg-[#008080] hover:bg-[#006666]"><Send className="h-4 w-4" /></Button>
                </div>
              ) : (
                <div className="p-3 border-t border-slate-100 text-center text-xs text-slate-400">{t("support.closedNote")}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
